import { useState, useEffect } from 'react';
import { SITE_TITLE, pageTitle } from '../../shared/constants/site';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { applySkillOrder } from '../../shared/utils/skillOrder';
import PageLayout from '../../components/layout/PageLayout/PageLayout';
import { C, ff, t } from '../Talent/Dashboard/components/dashboardConstants';
import { useAuth } from '../../contexts/AuthContext';
import { getTalentDetail, recordTalentVisit } from '../../api';
import { useSavedItems } from '../../contexts/SavedItemsContext';
import RoleOnlyModal from '../../shared/components/RoleOnlyModal';
import StartMessageButton from '../../shared/messages/StartMessageButton';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import BoardPickerModal, { SavedConfirmModal } from '../../shared/components/BoardPickerModal';
import ImageLightbox from '../Talent/Profile/components/ImageLightbox';
import FadeImage from '../../shared/components/FadeImage';
import { imageProtectionProps, protectedImageProps } from '../../shared/utils/imageProtection';
import styles from './TalentDetailPage.module.css';


const GENDER_MAP = { MALE: '남성', FEMALE: '여성', male: '남성', female: '여성' };

function formatPhone(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 11) return `${digits.slice(0,3)}-${digits.slice(3,7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
  return value;
}

function mapTalentDetail(item) {
  const medias = item.medias || [];
  // PROFILE_CLOSE_UP 미디어가 실제로 있을 때만 프로필 이미지 사용
  // (삭제 후 서버가 profileImageUrl에 다른 사진을 채워도 무시)
  const profileCloseUpUrl = medias.find(m => m.photoType === 'PROFILE_CLOSE_UP' && m.url)?.url ?? null;
  const mainPhotoUrl = profileCloseUpUrl
    ?? (medias.length === 0 ? (item.profileImageUrl ?? null) : null);
  const additionalPhotos = medias
    .filter(m => m.mediaType === 'IMAGE' && m.photoType === 'PORTFOLIO' && m.url)
    .map(m => m.url);
  const photos = [mainPhotoUrl, ...additionalPhotos].filter(Boolean);

  // FILMOGRAPHY media를 workId 기준으로 인덱싱
  const filmographyMediaByWorkId = {};
  medias
    .filter(m => m.photoType === 'FILMOGRAPHY' && m.workId && m.url)
    .forEach(m => { filmographyMediaByWorkId[m.workId] = m; });

  return {
    id: item.id,
    // 메시지 시작에 필요한 '계정' ID. 현재 공개 API 응답에는 없어 null이 된다.
    userId: item.userId ?? item.user?.id ?? null,
    slug: item.slug || String(item.id),
    name: item.name,
    stageName: item.stageName || '',
    verified: false,
    gender: GENDER_MAP[item.gender] || item.gender || '-',
    birthYear: item.birthDate ? new Date(item.birthDate).getFullYear() : null,
    age: item.birthDate ? String(new Date().getFullYear() - new Date(item.birthDate).getFullYear()) : '-',
    height: item.heightCm ? `${item.heightCm}cm` : '-',
    weight: item.weightKg ? `${item.weightKg}kg` : '-',
    tags: applySkillOrder(item.id, (item.skills || []).map(s => s.name || s.skillName || s).filter(Boolean)),
    agency: item.agency || '-',
    location: item.residence || '-',
    rating: item.avgRating || 0,
    reviews: 0,
    intro: item.bio || '소개가 없습니다.',
    photos,
    contactEmail: item.contactEmail || item.contact_email || item.email || item.emailAddress || item.user?.email || '',
    phone: item.phone || item.contactPhone || item.contact_phone || item.phoneNumber || item.mobile || item.user?.phone || '',
    instagramUrl: item.instagramUrl || '',
    youtubeUrl: item.youtubeUrl || '',
    filmography: (item.works || []).map(w => {
      // work.stillcutUrl 우선, 없으면 media.workId 기준으로 매핑
      const matchedMedia = filmographyMediaByWorkId[w.id];
      const imageUrl = w.stillcutUrl ?? matchedMedia?.url ?? null;
      return {
        title: w.title || '',
        role: w.role || w.roleName || '',
        year: String(w.year || w.releaseYear || ''),
        mediaType: w.category || '',
        imageUrl,
        youtubeUrl: w.videoUrl ?? null,
      };
    }).sort((a, b) => Number(b.year || 0) - Number(a.year || 0)),
  };
}

const DEFAULT_TALENT = {
  name: '배우 정보', stageName: '', verified: false, gender: '-', age: '-',
  birthYear: null, height: '-', weight: '-',
  tags: [], agency: '-', location: '-', rating: 0, reviews: 0,
  intro: '정보가 없습니다.', photos: [],
  contactEmail: '', phone: '', instagramUrl: '', youtubeUrl: '',
  filmography: [],
};

function StarRating({ value, reviewCount }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {[1,2,3,4,5].map(i => (
        <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill={i <= Math.round(value) ? C.point : C.g200}>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
      <span style={{ ...t('14px', 600, C.bk), marginLeft: '4px' }}>{value}</span>
      <span style={{ ...t('13px', 400, C.sub), marginLeft: '2px' }}>({reviewCount}개 리뷰)</span>
    </div>
  );
}

// 4개 슬롯 썸네일 — 메인 사진(photos[0]) 제외, photos[1]~[4] 표시
function ThumbnailRow({ photos, onSelect }) {
  const slots = Array.from({ length: 4 }, (_, i) => photos[i + 1] || null);
  return (
    <div className={styles.responsiveThumbnailRow}>
      {slots.map((url, i) => (
        <div
          key={i}
          onClick={() => url && onSelect(i + 1)}
          style={{
            flex: 1,
            aspectRatio: '1/1',
            borderRadius: '10px',
            overflow: 'hidden',
            border: `2px solid ${url ? C.g300 : C.g200}`,
            background: C.g200,
            cursor: url ? 'pointer' : 'default',
          }}
        >
          {url && (
            <img
              src={url}
              alt={`${i + 2}번째`}
              {...protectedImageProps({ width: '100%', height: '100%', objectFit: 'cover' })}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export default function TalentDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { role, profileId, isLoggedIn, isTalent, isProducer: hasProducerRole, switchRole } = useAuth();
  const [activeTab, setActiveTab] = useState('소개');
  const [talent, setTalent] = useState(DEFAULT_TALENT);
  const [talentId, setTalentId] = useState(null); // 실제 숫자 ID (API 응답에서)
  const [displayPhotos, setDisplayPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [hoveredContact, setHoveredContact] = useState(null);
  const [copiedContact, setCopiedContact] = useState(null);
  const isOwner = talentId != null && String(profileId) === String(talentId);
  const isProducer = role === 'PRODUCER';
  const canSeePrivateInfo = isOwner || isProducer;
  const { talentMap, toggleSave, boardPickerState, savedConfirmState, selectBoard, createAndSelectBoard, closeBoardPicker, closeSavedConfirm } = useSavedItems();
  const savedEntry = talentId ? talentMap[String(talentId)] : null;
  const isSaved = Boolean(savedEntry);
  const savedColor = savedEntry?.boardColor || null;
  const [saveBtnHovered, setSaveBtnHovered] = useState(false);
  const [imageModalUrl, setImageModalUrl] = useState(null);
  const [photoModalIndex, setPhotoModalIndex] = useState(null);
  const [roleModalState, setRoleModalState] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setIsLoading(true);
    setIsError(false);

    getTalentDetail(slug, role)
      .then(detail => {
        const mapped = mapTalentDetail(detail);
        setTalent(mapped);
        setTalentId(mapped.id);
        setDisplayPhotos(mapped.photos);
        const displayName = mapped.stageName || mapped.name;
        if (displayName) document.title = pageTitle(displayName);
        // 숫자 ID로 진입했으면 slug URL로 교체 (브라우저 히스토리 오염 방지)
        if (mapped.slug && slug !== mapped.slug) {
          navigate(`/talents/${mapped.slug}`, { replace: true, state: location.state });
        }
      })
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));

    // 떠날 때 사이트 제목으로 되돌린다 — 'Castar'로 하드코딩하면 제목을 바꿔도 여기서 덮어쓴다
    return () => { document.title = SITE_TITLE; };
  }, [slug]);

  useEffect(() => {
    if (!talentId) return;
    recordTalentVisit(talentId).catch(() => {});
  }, [talentId]);

  // Step 4 — 메인 사진 preload (첫 진입 시 빠른 렌더링)
  useEffect(() => {
    const url = displayPhotos[0];
    if (!url) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = url;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, [displayPhotos[0]]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPhotoModal = (idx) => setPhotoModalIndex(idx);
  const closePhotoModal = () => setPhotoModalIndex(null);
  const showPrevPhoto = () => {
    setPhotoModalIndex(prev => {
      if (prev == null || displayPhotos.length === 0) return prev;
      return (prev - 1 + displayPhotos.length) % displayPhotos.length;
    });
  };
  const showNextPhoto = () => {
    setPhotoModalIndex(prev => {
      if (prev == null || displayPhotos.length === 0) return prev;
      return (prev + 1) % displayPhotos.length;
    });
  };

  const handleSaveToggle = () => {
    if (role === 'TALENT' || !role) {
      setRoleModalState({
        title: '제작사 계정 전용 기능',
        message: role === 'TALENT' ? '배우 저장은 제작사 계정에서만 이용할 수 있습니다.' : '제작사로 로그인해서 이용해주세요.',
      });
      return;
    }
    toggleSave(talentId, 'TALENT_PROFILE');
  };

  const talentOnlyRoleModalProps = {
    hasBothRoles: isLoggedIn && isTalent && hasProducerRole,
    onSwitch: () => { setRoleModalState(null); switchRole('PRODUCER'); window.location.href = '/producer/dashboard'; },
    switchLabel: '제작사로 계정 전환하기',
    onboardingPath: isLoggedIn && !hasProducerRole ? '/onboarding/producer' : undefined,
    onboardingLabel: '제작사 계정 만들러 가기',
  };

  const TABS = ['소개', '필모그래피'];

  if (isLoading) {
    return (
      <PageLayout>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 40px 80px', fontFamily: ff, display: 'flex', justifyContent: 'center', paddingTop: '120px' }}>
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    );
  }

  if (isError) {
    return (
      <PageLayout>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 40px 80px', fontFamily: ff, textAlign: 'center', paddingTop: '120px' }}>
          <div style={t('16px', 600, C.bk)}>프로필을 불러오지 못했습니다.</div>
          <button onClick={() => navigate(-1)} style={{ marginTop: '16px', padding: '10px 24px', borderRadius: '10px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', ...t('14px', 500, C.bk) }}>
            돌아가기
          </button>
        </div>
      </PageLayout>
    );
  }

  function toInstagramUrl(val) {
    if (!val) return '';
    if (val.startsWith('http')) return val;
    const username = val.replace(/^@/, '');
    return `https://www.instagram.com/${username}/`;
  }

  function toYoutubeUrl(val) {
    if (!val) return '';
    if (val.startsWith('http')) return val;
    return `https://www.youtube.com/@${val.replace(/^@/, '')}`;
  }

  const contacts = [
    talent.contactEmail && { type: 'email', href: null, label: talent.contactEmail },
    talent.phone && { type: 'phone', href: null, label: formatPhone(talent.phone) },
    talent.instagramUrl && { type: 'instagram', href: toInstagramUrl(talent.instagramUrl), label: talent.instagramUrl.startsWith('http') ? talent.instagramUrl : `@${talent.instagramUrl.replace(/^@+/, '')}` },
    talent.youtubeUrl && { type: 'youtube', href: toYoutubeUrl(talent.youtubeUrl), label: talent.youtubeUrl },
  ].filter(Boolean);

  return (
    <PageLayout>
      {roleModalState && <RoleOnlyModal title={roleModalState.title} message={roleModalState.message} onClose={() => setRoleModalState(null)} {...talentOnlyRoleModalProps} />}
      {boardPickerState && (
        <BoardPickerModal
          boards={boardPickerState.options}
          targetType={boardPickerState.targetType}
          onClose={closeBoardPicker}
          onSelect={selectBoard}
          onCreateAndSelect={createAndSelectBoard}
        />
      )}
      {savedConfirmState && (
        <SavedConfirmModal
          boardName={savedConfirmState.boardName}
          onClose={closeSavedConfirm}
          onNavigateToArchive={() => {
            try { localStorage.setItem('producer-bookmark-open-board', String(savedConfirmState.boardId)); } catch {}
            closeSavedConfirm();
            navigate('/producer/archive');
          }}
        />
      )}
      <div className={styles.responsivePage} style={{ fontFamily: ff }}>
        {/* Back */}
        <button
          onClick={() => {
            sessionStorage.setItem('talents_from', 'detail');
            const from = location.state?.from;
            if (from && from.startsWith('/talents') && !from.startsWith('/talents/')) {
              navigate(-1);
            } else {
              navigate('/talents');
            }
          }}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '32px', ...t('15px', 500, C.sub) }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke={C.sub} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          배우 목록으로
        </button>

        <div className={styles.responsiveLayout}>
          {/* Left: photo section (460px) */}
          <div className={styles.responsivePhotoColumn}>
            {/* Main photo */}
            <div
              onClick={() => displayPhotos[0] && openPhotoModal(0)}
              className={styles.responsiveMainPhoto}
              style={{ cursor: displayPhotos[0] ? 'pointer' : 'default' }}
            >
              <FadeImage
                src={displayPhotos[0]}
                alt={talent.stageName || talent.name}
                size="medium"
                placeholder="talent"
                width={460}
                height={613}
                {...imageProtectionProps}
              />
            </div>
            {/* 4-slot thumbnails — 클릭하면 전체보기 모달 */}
            <ThumbnailRow
              photos={displayPhotos}
              onSelect={openPhotoModal}
            />
          </div>

          {/* Right: info */}
          <div className={styles.responsiveInfoColumn}>
            {/* Name row */}
            <div className={styles.responsiveNameActions}>
              <div className={styles.responsiveNameGroup}>
                <h1 style={{ ...t('32px', 700, C.bk), margin: 0 }}>
                  {talent.stageName || talent.name}
                </h1>
                {talent.stageName && (
                  <span style={{ ...t('16px', 400, C.sub) }}>{talent.name}</span>
                )}
                {talent.verified && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '8px', background: '#EFF6FF' }}>
                    <span style={t('12px', 600, C.blue)}>인증됨</span>
                  </div>
                )}
              </div>
              {/* Save + Share icons */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleSaveToggle}
                  onMouseEnter={() => setSaveBtnHovered(true)}
                  onMouseLeave={() => setSaveBtnHovered(false)}
                  style={{ width: 40, height: 40, borderRadius: '10px', border: isSaved ? '1px solid #ffd384' : `1px solid ${C.g200}`, background: isSaved ? '#fff7e8' : C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isSaved ? '#ffb517' : (saveBtnHovered ? '#090909' : '#888896'), transition: 'color 0.15s, background 0.15s, border-color 0.15s' }}
                  aria-label={isSaved ? '저장 해제' : '저장하기'}
                >
                  {isSaved ? (
                    <svg width="18" height="20" viewBox="0 0 18 20" fill="currentColor" aria-hidden="true">
                      <path d="M3 1h12a2 2 0 0 1 2 2v15.5l-8-4.5-8 4.5V3a2 2 0 0 1 2-2z"/>
                    </svg>
                  ) : (
                    <svg width="18" height="20" viewBox="0 0 18 20" fill="none" aria-hidden="true">
                      <path d="M3 1h12a2 2 0 0 1 2 2v15.5l-8-4.5-8 4.5V3a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
                <button
                  onClick={() => {
                    const shareUrl = `${window.location.origin}/talents/${talent.slug}`;
                    navigator.clipboard?.writeText(shareUrl).then(() => {
                      setShareCopied(true);
                      setTimeout(() => setShareCopied(false), 2000);
                    });
                  }}
                  style={{ position: 'relative', width: 40, height: 40, borderRadius: '10px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="18" cy="5" r="3" stroke={C.bk} strokeWidth="1.5"/>
                    <circle cx="6" cy="12" r="3" stroke={C.bk} strokeWidth="1.5"/>
                    <circle cx="18" cy="19" r="3" stroke={C.bk} strokeWidth="1.5"/>
                    <path d="M8.59 13.51l6.83 3.98M15.41 6.51L8.59 10.49" stroke={C.bk} strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  {shareCopied && (
                    <span style={{ position: 'absolute', top: '-34px', left: '50%', transform: 'translateX(-50%)', background: '#222', color: '#fff', fontSize: '12px', fontFamily: 'Pretendard, sans-serif', padding: '5px 10px', borderRadius: '6px', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                      링크 복사됨
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Rating */}
            {/* <div style={{ marginBottom: '20px' }}>
              <StarRating value={talent.rating} reviewCount={talent.reviews} />
            </div> */}

            {/* Physical info */}
            <div className={styles.responsivePhysicalStrip} style={{ background: C.bg }}>
              {[
                { label: '성별', value: talent.gender },
                { label: '나이', value: talent.birthYear ? `${talent.age}세 (${talent.birthYear}년생)` : `${talent.age}세` },
                { label: '키', value: talent.height },
                { label: '몸무게', value: talent.weight },
              ].map((item, i, arr) => (
                <div key={item.label} className={styles.responsivePhysicalItem} style={{ borderRight: i < arr.length - 1 ? `1px solid ${C.g200}` : 'none' }}>
                  <span style={t('12px', 400, C.sub)}>{item.label}</span>
                  <span style={t('15px', 600, C.bk)}>{item.value}</span>
                </div>
              ))}
            </div>

            {/* Action buttons
                포트폴리오 편집기는 배우 모드 전용 — 제작사 계정이라도 배우로 전환해 로그인한
                상태에서 본인 프로필을 볼 때만 노출한다. */}
            {/* 남의 프로필 — 대화 시작.
                targetUserId(계정 ID)가 응답에 없으면 컴포넌트가 스스로 아무것도 렌더하지 않는다. */}
            {!isOwner && isLoggedIn && (
              <div style={{ display: 'flex', gap: '12px', marginBottom: '32px', flexWrap: 'wrap' }}>
                <StartMessageButton
                  targetUserId={talent?.userId ?? null}
                  targetLabel={talent?.stageName || talent?.name || '배우'}
                  messagesPath={role === 'PRODUCER' ? '/producer/messages' : '/talent/messages'}
                />
              </div>
            )}

            {isOwner && role === 'TALENT' && (
              <div style={{ display: 'flex', gap: '12px', marginBottom: '32px' }}>
                <button
                  onClick={() => navigate('/talent/profile/pdf-editor')}
                  style={{ flex: 1, height: '52px', borderRadius: '14px', border: 'none', background: C.bk, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', ...t('15px', 600, C.white) }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke={C.white} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  포트폴리오 편집
                </button>
              </div>
            )}

            {/* Tabs */}
            <div style={{ borderBottom: `1px solid ${C.g200}`, marginBottom: '24px', display: 'flex' }}>
              {TABS.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{ padding: '12px 20px', background: 'none', border: 'none', borderBottom: `2px solid ${activeTab === tab ? C.bk : 'transparent'}`, cursor: 'pointer', marginBottom: '-1px', ...t('15px', activeTab === tab ? 700 : 400, activeTab === tab ? C.bk : C.sub) }}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab content */}
            {activeTab === '소개' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* 자기소개 */}
                <div>
                  <div style={{ ...t('15px', 600, C.sub), marginBottom: '8px' }}>자기소개</div>
                  <p style={{ ...t('15px', 400, C.bk), margin: 0, lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>{talent.intro}</p>
                </div>

                {/* 특기 */}
                <div>
                  <div style={{ ...t('15px', 600, C.sub), marginBottom: '8px' }}>특기</div>
                  {talent.tags.length > 0 ? (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {talent.tags.map(tag => (
                        <span key={tag} style={{ padding: '6px 14px', borderRadius: '100px', border: `1px solid ${C.g200}`, ...t('13px', 500, C.bk) }}>{tag}</span>
                      ))}
                    </div>
                  ) : (
                    <span style={t('15px', 400, C.bk)}>-</span>
                  )}
                </div>

                {/* 소속 */}
                <div>
                  <div style={{ ...t('15px', 600, C.sub), marginBottom: '8px' }}>소속</div>
                  <span style={t('15px', 400, C.bk)}>{talent.agency}</span>
                </div>

                {/* 거주지 */}
                <div>
                  <div style={{ ...t('15px', 600, C.sub), marginBottom: '8px' }}>거주지</div>
                  <span style={{ 
                    ...t('15px', 400, canSeePrivateInfo ? C.bk : C.g400),
                    cursor: canSeePrivateInfo ? 'default' : 'not-allowed'
                  }}>
                    {canSeePrivateInfo ? talent.location : '제작사에게만 공개되는 정보입니다.'}
                  </span>
                </div>

                {/* Contact */}
                {contacts.length > 0 && (
                  <div>
                    <div style={{ ...t('15px', 600, C.sub), marginBottom: '10px' }}>Contact</div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      {contacts.map(({ type, href, label }) => {
                        const iconEl = (
                          <>
                            {type === 'email' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="2" y="4" width="20" height="16" rx="2" stroke={C.sub} strokeWidth="1.5"/><path d="M2 8l10 7 10-7" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round"/></svg>}
                            {type === 'phone' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round"/></svg>}
                            {type === 'instagram' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="2" y="2" width="20" height="20" rx="5" stroke={C.sub} strokeWidth="1.5"/><circle cx="12" cy="12" r="4" stroke={C.sub} strokeWidth="1.5"/><circle cx="17.5" cy="6.5" r="1" fill={C.sub}/></svg>}
                            {type === 'youtube' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 00-1.95 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58z" stroke={C.sub} strokeWidth="1.5"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" stroke={C.sub} strokeWidth="1.5" strokeLinejoin="round"/></svg>}
                          </>
                        );
                        
                        const boxStyle = { 
                          width: 36, 
                          height: 36, 
                          borderRadius: '8px', 
                          border: `1px solid ${C.g200}`, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          textDecoration: 'none', 
                          cursor: 'pointer', 
                          background: 'none',
                          position: 'relative'
                        };
                        
                        const canSeeThisContact = (type === 'instagram' || type === 'youtube' || type === 'email' || type === 'web') ? true : canSeePrivateInfo;
                        const displayLabel = canSeeThisContact ? label : '제작사 전용';

                        const tooltipText = copiedContact === type ? '복사되었습니다' : displayLabel;
                        const tooltipEl = (hoveredContact === type || copiedContact === type) && canSeeThisContact && (
                          <div style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            marginBottom: '10px',
                            padding: '6px 12px',
                            background: '#090909',
                            color: '#fff',
                            fontSize: '12px',
                            fontWeight: 500,
                            borderRadius: '6px',
                            whiteSpace: 'nowrap',
                            zIndex: 10,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            pointerEvents: 'none'
                          }}>
                            {tooltipText}
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              borderWidth: '5px',
                              borderStyle: 'solid',
                              borderColor: '#090909 transparent transparent transparent'
                            }} />
                          </div>
                        );

                        if (type === 'phone' || type === 'email') {
                          const isPrivateGated = type === 'phone';
                          const canCopy = isPrivateGated ? canSeePrivateInfo : true;
                          return (
                            <button
                              key={type}
                              onMouseEnter={() => setHoveredContact(type)}
                              onMouseLeave={() => setHoveredContact(null)}
                              onClick={() => {
                                if (!canCopy) {
                                  alert('제작사 계정으로 로그인 후 확인 가능합니다.');
                                  return;
                                }
                                navigator.clipboard?.writeText(label).then(() => {
                                  setCopiedContact(type);
                                  setTimeout(() => setCopiedContact(null), 2000);
                                });
                              }}
                              style={{...boxStyle, opacity: canCopy ? 1 : 0.5, cursor: canCopy ? 'pointer' : 'not-allowed'}}
                            >
                              {tooltipEl}
                              {iconEl}
                            </button>
                          );
                        }
                        return (
                          <a 
                            key={type} 
                            href={canSeeThisContact ? href : '#'} 
                            target={canSeeThisContact ? "_blank" : "_self"} 
                            rel="noreferrer" 
                            style={{...boxStyle, opacity: canSeeThisContact ? 1 : 0.5, cursor: canSeeThisContact ? 'pointer' : 'not-allowed'}} 
                            onMouseEnter={() => setHoveredContact(type)}
                            onMouseLeave={() => setHoveredContact(null)}
                            onClick={(e) => {
                              if (!canSeeThisContact) {
                                e.preventDefault();
                                alert('제작사 계정으로 로그인 후 확인 가능합니다.');
                              }
                            }}
                          >
                            {tooltipEl}
                            {iconEl}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === '필모그래피' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {talent.filmography.length === 0 ? (
                  <div style={t('15px', 400, C.sub)}>등록된 필모그래피가 없습니다.</div>
                ) : (
                  talent.filmography.map((film, i) => {
                    const cardImageUrl = film.imageUrl || null;
                    return (
                      <div key={i} style={{ padding: '16px 20px', border: `1px solid ${C.g200}`, borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          {cardImageUrl && (
                            <div
                              onClick={() => setImageModalUrl(cardImageUrl)}
                              style={{ width: '64px', height: '80px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0, cursor: 'pointer', border: `1px solid ${C.g200}` }}
                            >
                              <img src={cardImageUrl} alt={film.title} loading="lazy" decoding="async" width={64} height={80} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                          )}
                          <div>
                            <div style={t('16px', 600, C.bk)}>{film.title}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                              {film.mediaType && (
                                <span style={{ padding: '2px 8px', borderRadius: '6px', background: C.bg, border: `1px solid ${C.g200}`, ...t('12px', 500, C.sub) }}>{film.mediaType}</span>
                              )}
                              <span style={t('13px', 400, C.sub)}>{film.role}</span>
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {film.youtubeUrl && (
                            <button
                              onClick={() => window.open(film.youtubeUrl, '_blank')}
                              style={{ width: 32, height: 32, borderRadius: '8px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                              title="영상 보기"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.97C18.88 4 12 4 12 4s-6.88 0-8.59.45A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58 2.78 2.78 0 001.95 1.97C5.12 20 12 20 12 20s6.88 0 8.59-.45a2.78 2.78 0 001.95-1.97A29 29 0 0023 12a29 29 0 00-.46-5.58z" stroke={C.sub} strokeWidth="1.5"/>
                                <path d="M9.75 15.02l5.75-3.02-5.75-3.02v6.04z" fill={C.sub}/>
                              </svg>
                            </button>
                          )}
                          <span style={{ padding: '4px 10px', borderRadius: '8px', background: C.g200, ...t('13px', 500, C.bk) }}>{film.year}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            <ImageLightbox
              url={imageModalUrl}
              alt="필모그래피 스틸컷"
              onClose={() => setImageModalUrl(null)}
              fullScreen
            />
            <ImageLightbox
              url={photoModalIndex == null ? null : displayPhotos[photoModalIndex]}
              alt={`${talent.stageName || talent.name} 프로필 사진`}
              onClose={closePhotoModal}
              onPrev={displayPhotos.length > 1 ? showPrevPhoto : undefined}
              onNext={displayPhotos.length > 1 ? showNextPhoto : undefined}
              currentIndex={photoModalIndex == null ? undefined : photoModalIndex + 1}
              totalCount={displayPhotos.length}
              large
              keepHeaderVisible
              fullScreen
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
